'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { useFormatter, useTranslations } from 'next-intl'
import type { ReactNode } from 'react'

import { CandlesIcon } from '@/components/shell-icons'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/features/auth/hooks'
import { HOME_EASE, HOME_REVEAL, LiveDot } from '@/features/home/components/home-motion'
import type { HomeSnapshot } from '@/features/home/hooks/use-home-snapshot'
import { useRelativeTime } from '@/features/home/hooks/use-relative-time'
import { countActivitySince } from '@/features/home/lib/home-activity'
import { useNow } from '@/hooks/use-now'
import { useNumberFormat } from '@/hooks/use-number-format'
import { cn } from '@/lib/utils'
import type { HomeVisit } from '@/state/home'

const MIN_VISIT_GAP_MS = 60_000

type DayPart = 'morning' | 'afternoon' | 'evening' | 'night'

function dayPart(hour: number): DayPart {
  if (hour >= 5 && hour < 12) return 'morning'
  if (hour >= 12 && hour < 18) return 'afternoon'
  if (hour >= 18 && hour < 23) return 'evening'
  return 'night'
}

/** "enes.aktas@…" → "Enes"; a one-word local part ("hello@…") is no name, so none is shown. */
function firstNameFromEmail(email: string | undefined): string | null {
  const words = (email?.split('@')[0] ?? '').split(/[._+-]+/).filter(Boolean)
  if (words.length < 2 || !/^[a-z]{2,}$/i.test(words[0])) return null
  return words[0][0].toUpperCase() + words[0].slice(1).toLowerCase()
}

function Chip({ children, tone }: { children: ReactNode; tone?: 'up' | 'down' }) {
  return (
    <motion.span
      layout
      initial={{ opacity: 0, y: 6, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.26, ease: HOME_EASE }}
      className={cn(
        'bg-foreground/5 text-foreground/80 inline-flex h-6 items-center gap-1 rounded-full px-2.5 text-xs whitespace-nowrap',
        tone === 'up' && 'bg-chart-mint/10 text-chart-mint',
        tone === 'down' && 'bg-chart-red/10 text-chart-red'
      )}
    >
      {children}
    </motion.span>
  )
}

interface HomeHeroProps {
  snapshot: HomeSnapshot
  /** What Home saw when it was last left; null on the first visit. */
  previousVisit: HomeVisit | null
  onOpenTrading: () => void
  onConnectExchange: () => void
}

/**
 * Greeting, today's date and the one line that answers "what happened while I was away?":
 * fills, new and cancelled orders since the last visit and how the portfolio moved.
 */
export function HomeHero({
  snapshot,
  previousVisit,
  onOpenTrading,
  onConnectExchange,
}: HomeHeroProps) {
  const t = useTranslations('trade.home.hero')
  const format = useFormatter()
  const fmt = useNumberFormat()
  const now = useNow(60_000)
  const relative = useRelativeTime()
  const { user } = useAuth()
  const name = firstNameFromEmail(user?.email)
  const greeting = t(`greeting.${dayPart(new Date(now).getHours())}`)

  const { portfolio, activity } = snapshot
  const hasAccounts = portfolio.accountCount > 0
  // A reload or a quick look away isn't a "visit" worth summarizing.
  const lastVisit =
    previousVisit && now - previousVisit.at >= MIN_VISIT_GAP_MS ? previousVisit : null
  const since = lastVisit ? countActivitySince(activity, lastVisit.at) : null
  const equityChange =
    lastVisit?.equityUsd != null && portfolio.equityUsd !== null
      ? portfolio.equityUsd - lastVisit.equityUsd
      : null
  const fills = since ? since.fill + since.liquidation : 0
  const quiet = since && fills === 0 && since.placed === 0 && since.cancelled === 0

  return (
    <motion.header
      variants={HOME_REVEAL}
      className="flex flex-col gap-5 pt-6 pb-2 md:flex-row md:items-end md:justify-between md:pt-10"
    >
      <div className="flex min-w-0 flex-col gap-3">
        <p className="text-foreground/50 flex items-center gap-2 text-xs tracking-tight">
          <LiveDot className="text-chart-mint" />
          {format.dateTime(now, { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
        <h1 className="font-display text-foreground text-3xl leading-tight tracking-tight md:text-[2.75rem]">
          {name ? t('greetingNamed', { greeting, name }) : greeting}
        </h1>
        <div className="flex min-h-6 flex-wrap items-center gap-1.5 text-sm">
          <AnimatePresence mode="popLayout" initial={false}>
            {!hasAccounts && !portfolio.isAccountsLoading ? (
              <motion.span key="welcome" layout className="text-foreground/60">
                {t('welcome')}
              </motion.span>
            ) : lastVisit && since ? (
              <motion.span key="since" layout className="text-foreground/60 mr-1">
                {t('sinceLastVisit', { time: relative(lastVisit.at) })}
              </motion.span>
            ) : (
              <motion.span key="first" layout className="text-foreground/60">
                {t('firstVisit')}
              </motion.span>
            )}
            {since && quiet && <Chip key="quiet">{t('quiet')}</Chip>}
            {since && fills > 0 && <Chip key="fills">{t('fills', { count: fills })}</Chip>}
            {since && since.placed > 0 && (
              <Chip key="placed">{t('placed', { count: since.placed })}</Chip>
            )}
            {since && since.cancelled > 0 && (
              <Chip key="cancelled">{t('cancelled', { count: since.cancelled })}</Chip>
            )}
            {since && equityChange !== null && Math.abs(equityChange) >= 0.01 && (
              <Chip key="equity" tone={equityChange > 0 ? 'up' : 'down'}>
                {t(equityChange > 0 ? 'portfolioUp' : 'portfolioDown', {
                  amount: fmt.usd(Math.abs(equityChange), { sensitive: true }),
                })}
              </Chip>
            )}
          </AnimatePresence>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {!hasAccounts && !portfolio.isAccountsLoading && (
          <Button variant="secondary" size="sm" onClick={onConnectExchange}>
            {t('connectExchange')}
          </Button>
        )}
        <Button size="sm" onClick={onOpenTrading} className="group">
          <CandlesIcon className="transition-transform duration-200 ease-(--ease-spring) group-hover:-rotate-6" />
          {t('openTrading')}
        </Button>
      </div>
    </motion.header>
  )
}
